import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { BookOpenIcon, ArrowLeftIcon, PlusIcon, MagnifyingGlassIcon, ChevronRightIcon, ChevronDownIcon, TrashIcon, ExclamationTriangleIcon, PencilSquareIcon } from '@heroicons/react/24/outline';
import { standardsAPI } from '../services/standardsApi';
import { useAuth } from '../contexts/AuthContext';
import AddRequirementModal from '../components/AddRequirementModal';
import EditRequirementModal from '../components/EditRequirementModal';
import toast from 'react-hot-toast';
import { getApiErrorMessage } from '../utils/apiError';
import {
  buildRequirementsTree,
  formatPointsDisplay,
  getComplianceTypeForDisplay,
  getImportMeta,
  getPointsForDisplay,
} from '../utils/requirementTree';

// Copy of the main app's client/src/pages/StandardDetailPage.js (plan 18.3), trimmed: the
// header lists only Back, Add Requirement and Delete Standard (no Linkages link, no AI
// Prompt button — neither has a route in this demo's route table, 19.5), and the scoring-
// basis pill/field are dropped since GRIHA's import_metadata never carries scoring_basis /
// well_points_basis (those are WELL-only fields from the main app). One addition beyond the
// copy: an "Observable check-items" chip list under the criteria block, read from
// import_metadata.observable_items (18.3).

// Helper function to filter tree based on search term
const filterTree = (nodes, searchTerm) => {
  if (!searchTerm.trim()) return nodes;

  const lowerSearch = searchTerm.toLowerCase();

  const matchesSearch = (req) => {
    return (
      req.title?.toLowerCase().includes(lowerSearch) ||
      req.section?.toLowerCase().includes(lowerSearch) ||
      req.description?.toLowerCase().includes(lowerSearch) ||
      req.criteria?.toLowerCase().includes(lowerSearch)
    );
  };

  const filterNode = (node) => {
    // Check if any children match
    const filteredChildren = node.children
      .map(filterNode)
      .filter(child => child !== null);

    // Include this node if it matches or has matching children
    if (matchesSearch(node) || filteredChildren.length > 0) {
      return { ...node, children: filteredChildren };
    }
    return null;
  };

  return nodes.map(filterNode).filter(node => node !== null);
};

// Recursive component to render a requirement and its children
const RequirementNode = ({ requirement, level = 0, expandedNodes, toggleNode, isAdmin, onDeleteClick, onEditClick, highlightedId }) => {
  const hasChildren = requirement.children && requirement.children.length > 0;
  const isExpanded = expandedNodes.has(requirement.id);
  const meta = getImportMeta(requirement);
  const pointsLabel = formatPointsDisplay(getPointsForDisplay(requirement));
  const compliance = getComplianceTypeForDisplay(requirement);
  const kind = meta.type;
  const applies = Array.isArray(meta.applies_to) ? meta.applies_to : null;
  const observableItems = Array.isArray(meta.observable_items) ? meta.observable_items : null;

  // Count total children recursively
  const countAllChildren = (node) => {
    let count = node.children?.length || 0;
    node.children?.forEach(child => {
      count += countAllChildren(child);
    });
    return count;
  };
  const totalChildCount = countAllChildren(requirement);

  return (
    <div id={`requirement-${requirement.id}`} className={level > 0 ? 'border-l-2 border-gray-200 ml-4' : ''}>
      <div
        className={`p-4 hover:bg-[#F8F8FF] ${level > 0 ? 'pl-4' : ''} ${
          highlightedId === requirement.id ? 'bg-amber-50 ring-2 ring-inset ring-amber-300' : ''
        }`}
        data-highlighted={highlightedId === requirement.id ? 'true' : undefined}
      >
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-start">
            {hasChildren && (
              <button
                type="button"
                onClick={() => toggleNode(requirement.id)}
                aria-expanded={isExpanded}
                aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${requirement.section}`}
                className="mr-2 mt-1 text-gray-500 hover:text-gray-700 focus:outline-none"
              >
                {isExpanded ? (
                  <ChevronDownIcon className="h-4 w-4" />
                ) : (
                  <ChevronRightIcon className="h-4 w-4" />
                )}
              </button>
            )}
            {!hasChildren && <div className="w-6 mr-2" />}
            <div>
              <h3 className={`font-medium text-gray-900 ${level === 0 ? 'text-lg' : 'text-base'}`}>
                {requirement.section} - {requirement.title}
              </h3>
              {hasChildren && (
                <span className="text-xs text-gray-500">
                  {requirement.children.length} sub-requirement{requirement.children.length !== 1 ? 's' : ''}
                </span>
              )}
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-1.5 ml-4 flex-shrink-0">
            {requirement.mandatory && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                Required
              </span>
            )}
            {kind && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                {kind}
              </span>
            )}
            {compliance && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-900" title="How parts or options combine for this node">
                {compliance}
              </span>
            )}
            {pointsLabel && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-900">
                {pointsLabel}
              </span>
            )}
            {isAdmin && (
              <>
                <button
                  type="button"
                  onClick={() => onEditClick(requirement)}
                  className="p-1 text-gray-400 hover:text-blue-600 focus:outline-none"
                  title="Edit requirement"
                >
                  <PencilSquareIcon className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => onDeleteClick(requirement, totalChildCount)}
                  className="p-1 text-gray-400 hover:text-red-600 focus:outline-none"
                  title="Delete requirement"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </>
            )}
          </div>
        </div>

        <div className="ml-8">
          {applies && applies.length > 0 && (
            <p className="text-xs text-gray-500 mb-2">
              <span className="font-medium text-gray-600">Applies to:</span> {applies.join(', ')}
            </p>
          )}
          <p className="text-sm text-gray-700 mb-3">
            {requirement.description}
          </p>

          <div className="bg-[#F8F8FF] rounded-md p-3">
            <h4 className="text-sm font-medium text-gray-900 mb-1">Criteria:</h4>
            <p className="text-sm text-gray-700 whitespace-pre-line">
              {requirement.criteria}
            </p>
          </div>

          {observableItems && observableItems.length > 0 && (
            <div className="mt-3">
              <h4 className="text-sm font-medium text-gray-900 mb-1.5">Observable check-items:</h4>
              <div className="flex flex-wrap gap-1.5">
                {observableItems.map((item, idx) => {
                  const text = typeof item === 'string' ? item : item?.text;
                  if (!text) return null;
                  const key = (typeof item === 'object' && item?.id) || `${requirement.id}-obs-${idx}`;
                  return (
                    <span
                      key={key}
                      title={typeof item === 'object' && item?.source ? item.source : undefined}
                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-teal-100 text-teal-900"
                    >
                      {text}
                    </span>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Render children if expanded */}
      {hasChildren && isExpanded && (
        <div className="ml-4">
          {requirement.children.map(child => (
            <RequirementNode
              key={child.id}
              requirement={child}
              level={level + 1}
              expandedNodes={expandedNodes}
              toggleNode={toggleNode}
              isAdmin={isAdmin}
              onDeleteClick={onDeleteClick}
              onEditClick={onEditClick}
              highlightedId={highlightedId}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default function StandardDetailPage() {
  const { standardId } = useParams();
  const navigate = useNavigate();
  const [showAddRequirementModal, setShowAddRequirementModal] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [showDeleteRequirementModal, setShowDeleteRequirementModal] = useState(false);
  const [showEditRequirementModal, setShowEditRequirementModal] = useState(false);
  const [requirementToEdit, setRequirementToEdit] = useState(null);
  const [requirementToDelete, setRequirementToDelete] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  // ?requirement=<id>: the Review Queue's View Criteria link (RUNLOG V8) opens the standard at
  // one appraisal - its ancestors expanded, the row highlighted and scrolled into view.
  const [searchParams] = useSearchParams();
  const focusRequirementId = searchParams.get('requirement');
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const isAdmin = user?.role === 'ADMIN';

  const { data: standardDetail, isLoading, error } = useQuery(
    ['standard', standardId],
    () => standardsAPI.getById(standardId),
    {
      select: (data) => data.data,
      retry: 1
    }
  );

  // Add requirement mutation
  const addRequirementMutation = useMutation(
    (requirementData) => standardsAPI.addRequirement(standardId, requirementData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['standard', standardId]);
        setShowAddRequirementModal(false);
        toast.success('Requirement added successfully!');
      },
      onError: (error) => {
        toast.error(getApiErrorMessage(error, 'Failed to add requirement'));
      }
    }
  );

  // Delete standard mutation
  const deleteStandardMutation = useMutation(
    () => standardsAPI.delete(standardId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('standards');
        toast.success('Standard deleted successfully!');
        navigate('/standards');
      },
      onError: (error) => {
        toast.error(getApiErrorMessage(error, 'Failed to delete standard'));
      }
    }
  );

  const handleDeleteStandard = () => {
    deleteStandardMutation.mutate();
    setShowDeleteConfirmModal(false);
  };

  const updateRequirementMutation = useMutation(
    ({ requirementId, payload }) => standardsAPI.updateRequirement(standardId, requirementId, payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['standard', standardId]);
        setShowEditRequirementModal(false);
        setRequirementToEdit(null);
        toast.success('Requirement updated successfully!');
      },
      onError: (error) => {
        toast.error(getApiErrorMessage(error, 'Failed to update requirement'));
      },
    }
  );

  // Delete requirement mutation
  const deleteRequirementMutation = useMutation(
    (requirementId) => standardsAPI.deleteRequirement(standardId, requirementId),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(['standard', standardId]);
        const message = data.data.children_deleted > 0
          ? `Requirement and ${data.data.children_deleted} sub-requirement(s) deleted successfully!`
          : 'Requirement deleted successfully!';
        toast.success(message);
      },
      onError: (error) => {
        toast.error(getApiErrorMessage(error, 'Failed to delete requirement'));
      }
    }
  );

  const handleDeleteRequirementClick = (requirement, childCount) => {
    setRequirementToDelete({ ...requirement, childCount });
    setShowDeleteRequirementModal(true);
  };

  const handleEditRequirementClick = (requirement) => {
    setRequirementToEdit(requirement);
    setShowEditRequirementModal(true);
  };

  const handleDeleteRequirement = () => {
    if (requirementToDelete) {
      deleteRequirementMutation.mutate(requirementToDelete.id);
      setShowDeleteRequirementModal(false);
      setRequirementToDelete(null);
    }
  };

  // Build requirements tree
  const requirementsTree = useMemo(() => {
    return buildRequirementsTree(standardDetail?.requirements);
  }, [standardDetail?.requirements]);

  useEffect(() => {
    if (!focusRequirementId || requirementsTree.length === 0) return;
    const pathTo = (nodes, trail) => {
      for (const node of nodes) {
        if (node.id === focusRequirementId) return [...trail, node];
        const found = pathTo(node.children || [], [...trail, node]);
        if (found) return found;
      }
      return null;
    };
    const path = pathTo(requirementsTree, []);
    if (!path) return;
    setExpandedNodes((prev) => {
      const next = new Set(prev);
      path.forEach((node) => {
        if (node.children && node.children.length > 0) next.add(node.id);
      });
      return next;
    });
  }, [focusRequirementId, requirementsTree]);

  // Scrolled to once per linked requirement, as soon as its row exists: expanding or
  // collapsing another criterion afterwards must not pull the page back to it.
  const scrolledTo = useRef(null);
  useEffect(() => {
    if (!focusRequirementId || scrolledTo.current === focusRequirementId) return;
    const element = document.getElementById(`requirement-${focusRequirementId}`);
    if (element && typeof element.scrollIntoView === 'function') {
      element.scrollIntoView({ block: 'center' });
      scrolledTo.current = focusRequirementId;
    }
  }, [focusRequirementId, expandedNodes]);

  // Filter tree based on search term
  const filteredTree = useMemo(() => {
    return filterTree(requirementsTree, searchTerm);
  }, [requirementsTree, searchTerm]);

  // Count only root/parent requirements (not including children)
  const rootRequirementsCount = requirementsTree.length;

  // Count visible root requirements after filtering
  const filteredRootCount = filteredTree.length;

  // Toggle expand/collapse for a node
  const toggleNode = (nodeId) => {
    setExpandedNodes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(nodeId)) {
        newSet.delete(nodeId);
      } else {
        newSet.add(nodeId);
      }
      return newSet;
    });
  };

  // Expand all nodes
  const expandAll = () => {
    const allIds = new Set();
    const collectIds = (nodes) => {
      nodes.forEach(node => {
        if (node.children && node.children.length > 0) {
          allIds.add(node.id);
          collectIds(node.children);
        }
      });
    };
    collectIds(filteredTree);
    setExpandedNodes(allIds);
  };

  // Collapse all nodes
  const collapseAll = () => {
    setExpandedNodes(new Set());
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#F8F8FF]">
        <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !standardDetail) {
    return (
      <div className="min-h-screen bg-[#F8F8FF]">
        <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <BookOpenIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Standard not found</h3>
            <p className="mt-1 text-sm text-gray-500">
              The requested standard could not be found.
            </p>
            <div className="mt-6">
              <button
                onClick={() => navigate('/standards')}
                className="inline-flex items-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700"
              >
                <ArrowLeftIcon className="h-4 w-4 mr-2" />
                Back to Standards
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F8FF]">
      <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          {/* Back navigation and actions */}
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={() => navigate('/standards')}
              className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
            >
              <ArrowLeftIcon className="h-4 w-4 mr-1" />
              Back to Standards
            </button>
            {isAdmin && (
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => setShowAddRequirementModal(true)}
                  className="inline-flex items-center justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  Add Requirement
                </button>
                <button
                  onClick={() => setShowDeleteConfirmModal(true)}
                  className="inline-flex items-center justify-center rounded-md border border-red-300 bg-white px-4 py-2 text-sm font-medium text-red-700 shadow-sm hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                >
                  <TrashIcon className="h-4 w-4 mr-2" />
                  Delete Standard
                </button>
              </div>
            )}
          </div>

          {/* Title */}
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">{standardDetail.full_name}</h1>
            <p className="text-gray-600">{standardDetail.description}</p>
          </div>

          {/* Standard Info */}
          <div className="mt-6 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <dt className="text-sm font-medium text-gray-500">Standard Name</dt>
                <dd className="mt-1 text-sm text-gray-900">{standardDetail.name}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Version</dt>
                <dd className="mt-1 text-sm text-gray-900">{standardDetail.version}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Status</dt>
                <dd className="mt-1">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    standardDetail.is_active
                      ? 'bg-green-100 text-green-800'
                      : 'bg-[#F8F8FF] text-gray-800'
                  }`}>
                    {standardDetail.is_active ? 'Active' : 'Inactive'}
                  </span>
                </dd>
              </div>
            </div>
          </div>
        </div>

        {/* Requirements Section */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-4">
              <h2 className="text-lg font-medium text-gray-900">
                Requirements ({rootRequirementsCount})
              </h2>
              {filteredTree.some(node => node.children && node.children.length > 0) && (
                <div className="flex items-center space-x-2">
                  <button
                    type="button"
                    onClick={expandAll}
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    Expand All
                  </button>
                  <span className="text-gray-300">|</span>
                  <button
                    type="button"
                    onClick={collapseAll}
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    Collapse All
                  </button>
                </div>
              )}
            </div>

            {/* Search Bar */}
            {standardDetail.requirements && standardDetail.requirements.length > 0 && (
              <div className="max-w-md relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Search requirements..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            )}
          </div>

          {searchTerm && (
            <p className="mb-4 text-sm text-gray-600">
              {filteredRootCount} of {rootRequirementsCount} requirements match "{searchTerm}"
            </p>
          )}
        </div>

        {/* Requirements List */}
        {filteredTree.length > 0 ? (
          <div className="bg-white shadow-sm border border-gray-200 rounded-lg overflow-hidden">
            <div className="divide-y divide-gray-200">
              {filteredTree.map((requirement) => (
                <RequirementNode
                  key={requirement.id}
                  requirement={requirement}
                  level={0}
                  expandedNodes={expandedNodes}
                  toggleNode={toggleNode}
                  isAdmin={isAdmin}
                  onDeleteClick={handleDeleteRequirementClick}
                  onEditClick={handleEditRequirementClick}
                  highlightedId={focusRequirementId}
                />
              ))}
            </div>
          </div>
        ) : standardDetail.requirements && standardDetail.requirements.length > 0 ? (
          <div className="bg-white shadow-sm border border-gray-200 rounded-lg p-6">
            <div className="text-center py-8">
              <MagnifyingGlassIcon className="mx-auto h-8 w-8 text-gray-400 mb-2" />
              <h3 className="text-sm font-medium text-gray-900">No requirements found</h3>
              <p className="text-sm text-gray-500">
                No requirements match your search term "{searchTerm}".
              </p>
              <button
                onClick={() => setSearchTerm('')}
                className="mt-3 text-blue-600 hover:text-blue-500 text-sm font-medium"
              >
                Clear search
              </button>
            </div>
          </div>
        ) : (
          <div className="bg-white shadow-sm border border-gray-200 rounded-lg p-6">
            <div className="text-center py-8">
              <BookOpenIcon className="mx-auto h-8 w-8 text-gray-400 mb-2" />
              <h3 className="text-sm font-medium text-gray-900">No requirements</h3>
              <p className="text-sm text-gray-500">
                No requirements have been defined for this standard yet.
              </p>
              {isAdmin && (
                <button
                  onClick={() => setShowAddRequirementModal(true)}
                  className="mt-3 inline-flex items-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  Add First Requirement
                </button>
              )}
            </div>
          </div>
        )}

        {/* Add Requirement Modal */}
        <AddRequirementModal
          isOpen={showAddRequirementModal}
          onClose={() => setShowAddRequirementModal(false)}
          onSubmit={(data) => addRequirementMutation.mutate(data)}
          isSubmitting={addRequirementMutation.isLoading}
          standardName={standardDetail?.full_name || 'Selected Standard'}
        />

        <EditRequirementModal
          isOpen={showEditRequirementModal}
          onClose={() => {
            setShowEditRequirementModal(false);
            setRequirementToEdit(null);
          }}
          requirement={requirementToEdit}
          onSubmit={async (payload) => {
            if (!requirementToEdit) return;
            await updateRequirementMutation.mutateAsync({
              requirementId: requirementToEdit.id,
              payload,
            });
          }}
          isSubmitting={updateRequirementMutation.isLoading}
          standardName={standardDetail?.full_name || 'Selected Standard'}
        />

        {/* Delete Confirmation Modal */}
        {showDeleteConfirmModal && (
          <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                  <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-medium text-gray-900">Delete Standard</h3>
                </div>
              </div>

              <p className="text-sm text-gray-500 mb-4">
                Are you sure you want to delete <span className="font-semibold text-gray-700">{standardDetail?.full_name}</span>?
                This will permanently delete the standard and all <span className="font-semibold text-gray-700">{standardDetail?.requirements?.length || 0} requirements</span> associated with it.
              </p>

              <p className="text-sm text-red-600 mb-6">
                This action cannot be undone.
              </p>

              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirmModal(false)}
                  className="flex-1 bg-white py-2 px-4 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleDeleteStandard}
                  disabled={deleteStandardMutation.isLoading}
                  className="flex-1 bg-red-600 py-2 px-4 border border-transparent rounded-md text-sm font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {deleteStandardMutation.isLoading ? 'Deleting...' : 'Delete Standard'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Requirement Confirmation Modal */}
        {showDeleteRequirementModal && requirementToDelete && (
          <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                  <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-medium text-gray-900">Delete Requirement</h3>
                </div>
              </div>

              <p className="text-sm text-gray-500 mb-4">
                Are you sure you want to delete <span className="font-semibold text-gray-700">{requirementToDelete.section} - {requirementToDelete.title}</span>?
                {requirementToDelete.childCount > 0 && (
                  <span> This will also delete <span className="font-semibold text-gray-700">{requirementToDelete.childCount} sub-requirement{requirementToDelete.childCount !== 1 ? 's' : ''}</span>.</span>
                )}
              </p>

              <p className="text-sm text-red-600 mb-6">
                This action cannot be undone.
              </p>

              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowDeleteRequirementModal(false);
                    setRequirementToDelete(null);
                  }}
                  className="flex-1 bg-white py-2 px-4 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleDeleteRequirement}
                  disabled={deleteRequirementMutation.isLoading}
                  className="flex-1 bg-red-600 py-2 px-4 border border-transparent rounded-md text-sm font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {deleteRequirementMutation.isLoading ? 'Deleting...' : 'Delete Requirement'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
